import getLangLocale from 'I18n/utils/getLangLocale';
import { consoleIfNotProd } from 'chatspot-core/loggers/consoleIfNotProd';
import { formatChatspotApiMessage } from 'chatspot-core/messages/api/formatChatspotApiMessage';
import { SALES_COPILOT } from 'chatspot-core/types/CopilotAssistantTypes';
import { useCopilotAppContext } from 'copilot-toolkit/hooks/useCopilotAppContext';
import { useCopilotFreeContext } from 'copilot-toolkit/hooks/useCopilotFreeContext';
import Raven from 'raven-js';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useCopilotV2PublishAblyMessage } from '../../ably/hooks/useCopilotV2PublishAblyMessage';
import { selectAssistant, selectAssistantType } from '../../assistants/selectors/assistants';
import { Paths } from '../../components/Routes';
import { useHasSalesCopilotAccess } from '../../gating/hooks/useHasAccess';
import { useIsUngatedForCopilotV2 } from '../../gating/hooks/useIsUngatedForCopilotV2';
import { useWaitForExtraIntentProperties } from '../../hooks/useCollaborationParentState';
import { useHostAppContext } from '../../hostAppContext/hooks/useAppContext';
import { useGetTransformedInteractiveStateForBackend } from '../../threads/hooks/useInteractiveModeHooks';
import { getPageTitle, getPageUrl } from '../../utils/pageContextUtil';
import { usageTracker } from '../../utils/usageTracker';
import { sendSalesCopilotMessage } from '../actions/chatSalesCopilotMessageSent';
import { chatStreamingMessageSent } from '../actions/chatStreamingMessageSent';
import { selectActiveThreadId, selectMessages } from '../selectors/chatSelectors';
import { selectAppSearchScope } from '../selectors/chatv2Selectors';
import { chatEventStreamClosed, chatEventStreamError, chatEventStreamUpdate, setIsStreamingWithReasoningTimestamp } from '../slices/chat';
import { getMessageTracking } from '../utils/chatRequestUtil';
import { getMaybeFreeformContextFromExtraIntentProperties } from '../utils/collabModeUtil';
import { useIsSendMessageDisabled } from './useIsSendMessageDisabled';
import { useShouldSendCsatSurveyLegacy } from './useShouldSendCsatSurveyLegacy';
export const useSendChatMessage = () => {
  const dispatch = useDispatch();
  const {
    sourceHostApp,
    sourceHostAppPathname
  } = useHostAppContext();
  const isSendMessageDisabled = useIsSendMessageDisabled();
  const waitForExtraIntentProperties = useWaitForExtraIntentProperties();
  const history = useHistory();
  const messages = useSelector(selectMessages);
  const currentActiveThreadId = useSelector(selectActiveThreadId);
  const shouldSendCsatSurveyLegacy = useShouldSendCsatSurveyLegacy();
  const publishAblyMessage = useCopilotV2PublishAblyMessage();
  const assistantType = useSelector(selectAssistantType);
  const selectedAssistant = useSelector(selectAssistant);
  const hasSalesCopilotAccess = useHasSalesCopilotAccess();
  const isUngatedForCopilotV2 = useIsUngatedForCopilotV2();
  const appContext = useCopilotAppContext();
  const freeContext = useCopilotFreeContext();
  const interactiveModeInput = useGetTransformedInteractiveStateForBackend();

  // Get the app search scope from Redux state
  const appSearchScope = useSelector(selectAppSearchScope);
  return useCallback(async (message, {
    source,
    template,
    type,
    pageContext,
    isSavedPrompt
  }, threadId) => {
    if (isSendMessageDisabled) {
      return;
    }
    const extraIntentProperties = await waitForExtraIntentProperties().catch(e => {
      consoleIfNotProd.error('Error waiting for extra intent properties', e);
      return undefined;
    });
    const isCollaborateMode = extraIntentProperties && Object.keys(extraIntentProperties).length > 0 || false;
    history.push(Paths.CHAT);
    const onProgress = (event, data) => {
      if (event === 'progress' && data.reasoningEnabled && data.timestamp) {
        dispatch(setIsStreamingWithReasoningTimestamp(data.timestamp));
      } else if (event === 'message') {
        dispatch(chatEventStreamUpdate(formatChatspotApiMessage(data, source)));
      }
    };
    const onClose = (statusCode, event, data) => {
      var _data$query;
      usageTracker.track('chatspotCoreInteraction', {
        action: 'prompt_submitted',
        intent: data === null || data === void 0 || (_data$query = data.query) === null || _data$query === void 0 ? void 0 : _data$query.intent,
        successful: !(data !== null && data !== void 0 && data.error),
        error: data === null || data === void 0 ? void 0 : data.error,
        statusCode,
        source,
        type
      });
      dispatch(chatEventStreamClosed());
      shouldSendCsatSurveyLegacy();
    };
    const onError = (e, shouldStopStream) => {
      if (shouldStopStream) {
        dispatch(chatEventStreamError());
      }
      usageTracker.track('chatspotCoreInteraction', {
        action: 'streaming_error',
        error: e.message,
        source,
        type
      });
      Raven.captureException(e);
    };
    const isSalesCopilot = assistantType === SALES_COPILOT;
    if (isSalesCopilot && hasSalesCopilotAccess) {
      dispatch(sendSalesCopilotMessage({
        createThread: messages.length === 0,
        message,
        threadId: currentActiveThreadId
      })).catch(e => {
        consoleIfNotProd.error('Error sending sales copilot message', e);
      });
      usageTracker.track('chatspotCoreInteraction', {
        action: 'sales_copilot_message_sent',
        message,
        source,
        type
      });
    } else if (isUngatedForCopilotV2) {
      const freeformContext = isCollaborateMode ? getMaybeFreeformContextFromExtraIntentProperties({
        extraIntentProperties,
        message
      }) : freeContext;
      // TODO: Consolidate more of this in a util we can use for both sendMessageAction and sendChatMessage
      publishAblyMessage(Object.assign({
        context: {
          // TODO: @mlegocki: handle file-upload data
          files: [],
          clientType: 'DESKTOP',
          crmObjectContext: appContext.crmObjectContext,
          locale: getLangLocale(),
          freeformContext: JSON.stringify(freeformContext),
          pageContext: {
            title: getPageTitle(),
            url: getPageUrl(),
            sourceHostApp
          },
          collabMode: isCollaborateMode,
          chirpToolFqcns: [],
          appSearchScope
        }
      }, getMessageTracking({
        template,
        isSavedPrompt
      }), {
        customCopilotId: selectedAssistant === null || selectedAssistant === void 0 ? void 0 : selectedAssistant.id,
        message,
        type: 'CHAT',
        // TODO: This will be CHAT vs ACTION based on the JSON subtype of MessageRequestCore
        sourceHostAppPathname,
        // TODO: BE is not currently using this, but we had it for v1 - determine if it is needed for tracking in v2
        template,
        interactiveInput: interactiveModeInput
      }));
    } else {
      dispatch(chatStreamingMessageSent({
        appContext,
        locale: getLangLocale(),
        onError,
        onProgress,
        onClose,
        message,
        source,
        sourceHostApp,
        pageContext,
        sourceHostAppPathname,
        template,
        threadId: threadId !== null && threadId !== void 0 ? threadId : currentActiveThreadId,
        isCollaborateMode,
        extraIntentProperties,
        isSavedPrompt,
        freeContext: Object.assign({}, freeContext, extraIntentProperties)
      })).catch(e => {
        consoleIfNotProd.error('Error sending streaming message', e);
      });
    }
  }, [appContext, appSearchScope, assistantType, currentActiveThreadId, dispatch, freeContext, hasSalesCopilotAccess, history, interactiveModeInput, isSendMessageDisabled, isUngatedForCopilotV2, messages.length, publishAblyMessage, selectedAssistant === null || selectedAssistant === void 0 ? void 0 : selectedAssistant.id, shouldSendCsatSurveyLegacy, sourceHostApp, sourceHostAppPathname, waitForExtraIntentProperties]);
};